import { useContext, useEffect } from 'react';

import { CustomerReportContext } from 'components/grace/Contexts';
import Modal from 'components/grace/Modal';
import OpenButton from 'components/grace/OpenButton';
import SurveyModal from 'components/grace/SurveyModal';
import useBrowserPathname from 'hooks/useBrowserPathname';
import useScreenSizeConstants from 'hooks/useScreenSizeConstants';
import {
  isAdaStarted,
  markAdaChatDrawer,
  setAdaStarted,
  subscribeToAdaChatLifecycleEvents,
} from 'lib/adaChatSession';
import { ADA_UNAVAILABLE_ERROR, MODAL_TYPES } from 'lib/constants';
import * as AdaChat from 'utilities/adaChat';

import HelpInterstitialModal from './HelpInterstitialModal';

const Grace = () => {
  const { customerReport, currentStage, currentModal, setCurrentModal, setErrorMessage } =
    useContext(CustomerReportContext);
  const pathname = useBrowserPathname();
  const { isUnderBp2 } = useScreenSizeConstants();

  const isOnBoxOrPickMeals = /\/signup\/box(\/|$)/.test(pathname) || pathname === '/pick-meals';
  const isOnFunnel = /\/signup(\/|$)/.test(pathname);
  const shouldHideModal =
    /\/account\/calendar\/.+/.test(pathname) || (isOnBoxOrPickMeals && isUnderBp2);

  useEffect(() => {
    if (shouldHideModal || currentModal !== MODAL_TYPES.actual_ada) return;

    if (!window.adaEmbed) {
      setErrorMessage(ADA_UNAVAILABLE_ERROR);
      setCurrentModal(MODAL_TYPES.ada);
      return;
    }

    const openAda = async () => {
      const settingsToggleCallback = window.adaSettings?.toggleCallback;
      const onAdaDrawerToggle = isDrawerOpen => {
        settingsToggleCallback?.(isDrawerOpen);
        AdaChat.updateAdaIconVisibility({ hide: true });
        markAdaChatDrawer(isDrawerOpen);
        if (isDrawerOpen) {
          setCurrentModal('');
        } else {
          setCurrentModal(MODAL_TYPES.help);
        }
      };
      if (!isAdaStarted()) {
        await window.adaEmbed.start({
          ...window.adaSettings,
          toggleCallback: onAdaDrawerToggle,
          greeting: null,
          metaFields: {
            ...window.adaSettings.metaFields,
          },
        });
        setAdaStarted();
      }
      if (window.adaEmbed) {
        await subscribeToAdaChatLifecycleEvents(() => setCurrentModal(MODAL_TYPES.help));
        AdaChat.updateAdaIconVisibility({ hide: true });
        await window.adaEmbed.toggle();
      }
    };

    openAda().catch(() => {
      setErrorMessage(ADA_UNAVAILABLE_ERROR);
      setCurrentModal(MODAL_TYPES.ada);
    });
  }, [currentModal, setCurrentModal, setErrorMessage, shouldHideModal]);

  const renderModal = () => {
    switch (currentModal) {
      case MODAL_TYPES.grace:
        return customerReport && currentStage ? <Modal /> : isOnFunnel ? null : <OpenButton />;
      case MODAL_TYPES.survey:
        return <SurveyModal />;
      case MODAL_TYPES.ada:
        return <HelpInterstitialModal />;
      case MODAL_TYPES.cs:
        return <HelpInterstitialModal cs={true} />;
      case MODAL_TYPES.help:
        return isOnFunnel ? null : <OpenButton />;
      case MODAL_TYPES.actual_ada:
        return null;
      default:
        return null;
    }
  };

  return <>{!shouldHideModal && renderModal()}</>;
};

export default Grace;
